import { useRef } from 'react';
import { Check, Star } from 'lucide-react';
import { pricingPlans, planStyles } from '@/lib/pricingPlans';
import { planComparison } from '@/lib/planComparison';
import { useLanguage } from '@/lib/LanguageContext';

/**
 * Valor de una casilla: marca cuando el plan lo incluye, guion cuando no y el
 * nivel concreto cuando la diferencia es de grado (soporte, SLA).
 */
function PlanValue({ value, dark }) {
  if (value === true) {
    return (
      <span className={`inline-flex items-center justify-center w-6 h-6 rounded-full ${dark ? 'bg-brand-blue' : 'bg-gray-900'}`}>
        <Check size={13} strokeWidth={3} className="text-white" />
      </span>);

  }
  if (value === false) {
    return <span className={`text-sm ${dark ? 'text-white/30' : 'text-gray-300'}`}>—</span>;
  }
  return <span className={`text-sm font-semibold ${dark ? 'text-white' : 'text-gray-700'}`}>{value}</span>;
}

/**
 * Tabla comparativa de los tres planes, por grupos de características. En
 * pantallas estrechas se desliza en horizontal con las flechas o el dedo.
 */
export default function PlanComparison({ revealed = true }) {
  const scrollRef = useRef(null);
  const { lang } = useLanguage();

  const plans = (pricingPlans[lang] || pricingPlans.ES).plans.map((plan, i) => ({ ...plan, ...planStyles[i] }));
  const content = planComparison[lang] || planComparison.ES;

  const scroll = (direction) => {
    const el = scrollRef.current;
    if (el) el.scrollBy({ left: direction * el.clientWidth * 0.8, behavior: 'smooth' });
  };

  return (
    <div className={`mt-16 rounded-3xl bg-gray-50 border border-gray-100 p-6 lg:p-10 transition-all duration-700 delay-300 ${revealed ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-6 mb-8">
        <div>
          <h2 className="font-heading font-black text-2xl lg:text-3xl text-gray-900 tracking-tight mb-2">{content.title}</h2>
          <p className="text-gray-500 text-sm font-light max-w-xl">{content.subtitle}</p>
        </div>
        
















        
      </div>

      <div ref={scrollRef} className="overflow-x-auto">
        <table className="w-full min-w-[720px] table-fixed border-separate border-spacing-0">
          <colgroup>
            <col className="w-[28%]" />
            <col className="w-[24%]" />
            <col className="w-[24%]" />
            <col className="w-[24%]" />
          </colgroup>
          <thead>
            <tr>
              <th className="align-bottom" />
              {plans.map((plan, i) =>
              <th key={plan.name} className={`px-4 pt-6 pb-5 align-top font-normal ${i === 1 ? 'bg-gray-950' : ''}`}>
                  <div className="flex flex-col items-center gap-2">
                    {plan.badge &&
                  <span className="inline-flex items-center gap-1 rounded-full bg-brand-blue text-white text-[10px] font-black uppercase tracking-widest px-3 py-1">
                        <Star size={10} /> {plan.badge}
                      </span>
                  }
                    <span className={`text-xs font-black uppercase tracking-widest ${i === 1 ? 'text-white' : 'text-brand-blue'}`}>
                      {plan.name}
                    </span>
                    <span className={`text-[10px] font-bold uppercase tracking-widest ${i === 1 ? 'text-white/40' : 'text-gray-400'}`}>
                      {plan.tag}
                    </span>
                    <span className={`font-heading font-black text-2xl ${i === 1 ? 'text-white' : 'text-gray-900'}`}>
                      {plan.price}
                      <span className={`text-xs font-medium ${i === 1 ? 'text-white/50' : 'text-gray-400'}`}> {plan.period}</span>
                    </span>
                  </div>
                </th>
              )}
            </tr>
          </thead>
          {content.groups.map((group) =>
          <tbody key={group.name}>
              <tr>
                <td className="px-4 pt-8 pb-3 text-[11px] font-black uppercase tracking-widest text-gray-400">{group.name}</td>
                {plans.map((plan, i) =>
              <td key={plan.name} className={i === 1 ? 'bg-gray-950' : ''} />
              )}
              </tr>
              {group.rows.map((row) =>
            <tr key={row.label}>
                  <td className="px-4 py-3.5 text-sm font-light text-gray-700 border-b border-gray-200">{row.label}</td>
                  {row.values.map((value, i) =>
              <td
                key={plans[i].name}
                className={`px-4 py-3.5 text-center border-b ${i === 1 ? 'bg-gray-950 border-white/10' : 'border-gray-200'}`}>
                
                      <PlanValue value={value} dark={i === 1} />
                    </td>
              )}
                </tr>
            )}
            </tbody>
          )}
        </table>
      </div>
    </div>);

}